"use client"

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiUser, FiMail, FiPhone, FiMapPin, FiSave, FiUpload, FiBriefcase, FiBox, FiTool, FiTruck, FiCheckCircle, FiFileText, FiCamera, FiExternalLink, FiLoader, FiAlertCircle, FiEdit3, FiShield, FiStar, FiTrendingUp } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../../services/api';
import { uploadDocument as uploadDocumentService } from '../../services/documentService';
import PhoneVerification from './PhoneVerification';

// Données constantes
const SERVICES_OPTIONS = [
  { id: 'packing', label: 'Emballage', icon: <FiBox/> },
  { id: 'assembly', label: 'Montage de meubles', icon: <FiTool/> },
  { id: 'heavy_lift', label: 'Objets lourds / Piano', icon: <FiTruck/> }
];
const TRUCK_OPTIONS = [
  { id: 'van', label: 'Fourgon (<15m³)' },
  { id: 'small_truck', label: 'Camion (20m³)' },
  { id: 'medium_truck', label: 'Camion (30m³)' },
  { id: 'heavy_duty', label: 'Poids lourd (>50m³)' }
];

// --- Composants UI (à mettre dans un dossier /components/ui) ---

const Card = ({ children, className, delay = 0 }) => (
  <motion.div 
    initial={{ opacity: 0, y: 30, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, ease: "easeOut", delay }}
    whileHover={{ y: -2, scale: 1.02 }}
    className={`bg-gradient-to-br from-slate-800/90 via-slate-800/70 to-slate-900/90 backdrop-blur-xl border border-slate-700/50 rounded-3xl shadow-2xl overflow-hidden hover:shadow-amber-500/10 hover:border-amber-500/30 transition-all duration-300 ${className}`}
  >
    {children}
  </motion.div>
);

const CardHeader = ({ title, subtitle, icon, gradient = "from-amber-400 to-amber-600" }) => (
  <div className="p-6 border-b border-slate-700/80 bg-gradient-to-r from-slate-800/50 to-slate-900/30">
    <div className="flex items-center gap-4">
      {icon && (
        <div className={`text-white bg-gradient-to-br ${gradient} p-3 rounded-2xl shadow-lg ring-2 ring-amber-500/20`}>
          {icon}
        </div>
      )}
      <div className="flex-1">
        <h2 className="text-xl font-bold text-white tracking-wide">{title}</h2>
        {subtitle && <p className="text-sm text-amber-300/80 mt-1 font-medium">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const InputField = React.forwardRef(({ id, label, icon, ...props }, ref) => (
  <div className="group">
    <label htmlFor={id} className="block text-sm font-semibold text-slate-200 mb-3 tracking-wide">{label}</label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-amber-400/70 group-focus-within:text-amber-400 transition-colors">{icon}</div>
      <input 
        id={id} 
        ref={ref}
        className="w-full pl-12 pr-4 py-3.5 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300 text-white placeholder-slate-400 backdrop-blur-sm hover:border-slate-500/70 focus:shadow-lg focus:shadow-amber-500/20" 
        {...props} 
      />
    </div>
  </div>
));

const CheckboxOption = ({ id, checked, onChange, label, icon, disabled = false }) => (
  <label htmlFor={id} className={`flex items-center gap-4 p-4 bg-gradient-to-r from-slate-700/60 to-slate-800/60 rounded-2xl border-2 border-slate-600/50 has-[:checked]:border-amber-500/70 has-[:checked]:bg-gradient-to-r has-[:checked]:from-amber-500/10 has-[:checked]:to-amber-600/10 cursor-pointer transition-all duration-300 hover:border-slate-500/70 hover:shadow-lg hover:shadow-slate-500/10 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
    <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-xl border-2 border-slate-500/70 bg-slate-800/80 has-[:checked]:bg-gradient-to-br has-[:checked]:from-amber-500 has-[:checked]:to-amber-600 has-[:checked]:border-amber-500 shadow-inner">
      <AnimatePresence>
        {checked && (
          <motion.div 
            initial={{ scale: 0, rotate: -180 }} 
            animate={{ scale: 1, rotate: 0 }} 
            exit={{ scale: 0, rotate: 180 }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
          >
            <FiCheckCircle className="w-4 h-4 text-white drop-shadow-sm" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    <input type="checkbox" id={id} checked={checked} onChange={onChange} className="sr-only" disabled={disabled} />
    {icon && <span className="text-amber-400/80 text-lg">{icon}</span>}
    <span className="font-semibold text-sm text-slate-200 tracking-wide">{label}</span>
  </label>
);

const ToggleSwitch = ({ enabled, onChange, label }) => (
  <div className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-2xl">
    <label className="font-semibold text-slate-200 tracking-wide">{label}</label>
    <button 
      type="button" 
      onClick={onChange} 
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-all duration-300 shadow-lg ${enabled ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-slate-600 to-slate-700'}`}
    >
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-all duration-300 ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
);

// --- Composants Spécifiques au Profil (à mettre dans /components/profile) ---

const ProfileHeader = ({ user, avatar, onAvatarChange, onSave, isSubmitting }) => {
  const avatarInputRef = useRef(null);
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-800/90 to-slate-900/90 mb-8 shadow-2xl border border-slate-700/50 backdrop-blur-xl"
    >
      <div className="h-56 bg-cover bg-center relative" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1616047006789-b7af5afb8c20?q=80&w=2070&auto=format&fit=crop)' }}>
        <div className="h-full bg-gradient-to-b from-black/20 via-black/40 to-black/60"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-transparent"></div>
      </div>
      <div className="px-8 pb-8 pt-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between -mt-24">
          <div className="flex items-end space-x-6">
            <motion.div 
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.3, type: "spring", stiffness: 200 }}
              className="relative group"
            >
              <div className="w-36 h-36 rounded-3xl border-4 border-slate-800/80 bg-gradient-to-br from-slate-700 to-slate-800 overflow-hidden shadow-2xl ring-4 ring-amber-500/20">
                {avatar ? (
                  <img src={avatar} alt="Profil" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-500/20 to-amber-600/20">
                    <FiUser className="w-20 h-20 text-amber-400/70" />
                  </div>
                )}
              </div>
              <motion.button 
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                type="button" 
                onClick={() => avatarInputRef.current?.click()} 
                className="absolute -bottom-2 -right-2 bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white p-3 rounded-full shadow-xl transition-all duration-300 border-2 border-slate-800" 
                title="Changer la photo"
              >
                <FiCamera className="w-5 h-5" />
              </motion.button>
              <input type="file" ref={avatarInputRef} onChange={onAvatarChange} className="hidden" accept="image/*" />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mb-6"
            >
              <h1 className="text-4xl font-bold text-white tracking-wide mb-2">{user.firstName} {user.lastName}</h1>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"></div>
                <p className="text-amber-400 font-semibold text-lg">Profil Déménageur</p>
              </div>
            </motion.div>
          </div>
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-4 md:mt-0"
          >
            <motion.button 
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              type="submit" 
              onClick={onSave} 
              disabled={isSubmitting} 
              className="w-full md:w-auto flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 text-slate-900 font-bold rounded-2xl transition-all duration-300 shadow-xl hover:shadow-amber-500/25 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-amber-400/20"
            >
              {isSubmitting ? (
                <FiLoader className="animate-spin w-5 h-5" />
              ) : (
                <FiSave className="w-5 h-5" />
              )}
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </motion.button>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

const DocumentViewer = ({ docType, docUrl }) => {
  const isImage = docUrl && docUrl.match(/\.(jpe?g|png|gif|webp)$/i);
  
  if (!docUrl) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
      className="space-y-3 p-5 bg-gradient-to-br from-slate-700/60 to-slate-800/60 rounded-2xl border border-slate-600/50 backdrop-blur-sm hover:shadow-lg hover:shadow-slate-500/10 transition-all duration-300"
    >
      <div className="flex items-center justify-between">
        <span className="font-semibold text-slate-200 tracking-wide">{docType.label}</span>
        <motion.span 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30"
        >
          <FiCheckCircle className="mr-1.5 w-3 h-3" /> Téléchargé
        </motion.span>
      </div>
      
      <div className="mt-3 text-center">
        {isImage ? (
          <motion.img 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            src={docUrl} 
            alt={docType.label} 
            className="max-h-36 mx-auto rounded-xl border-2 border-slate-600/50 shadow-lg hover:shadow-xl transition-all duration-300"
          />
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col items-center p-6"
          >
            <div className="w-16 h-16 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-2xl flex items-center justify-center mb-3">
              <FiFileText className="h-8 w-8 text-amber-400" />
            </div>
            <span className="text-sm font-medium text-slate-300">Document téléchargé</span>
          </motion.div>
        )}
        <motion.a 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          href={docUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-4 py-2 rounded-xl border border-amber-500/30 hover:border-amber-500/50"
        >
          {isImage ? 'Voir l\'image' : 'Voir le document'} 
          <FiExternalLink className="ml-2 w-4 h-4" />
        </motion.a>
      </div>
    </motion.div>
  );
};

// --- Composant Principal de la Page ---

export default function DemProfil() {
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModificationRequest, setShowModificationRequest] = useState(false);
  const [modificationRequest, setModificationRequest] = useState('');
  const [requestedField, setRequestedField] = useState('');
  const [avatarPreview, setAvatarPreview] = useState('');
  const [documents, setDocuments] = useState({ cin: null, insurance: null, registration: null });
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', phone: '', company: '', address: '', city: '',
    postalCode: '', description: '', services: {}, fleet: {},
    isVisible: true, notificationsEnabled: true,
  });
  
  // --- LOGIQUE (useEffect, Handlers) ---
  useEffect(() => {
    // ... votre logique de chargement des données reste la même
    const loadUserData = async () => {
      if (!user) {
        window.location.href = '/login';
        return;
      }
      try {
        setIsLoading(true);
        const userData = await api.getUserProfile();
        console.log('User data loaded:', userData);
        
        if (!userData) throw new Error("Aucune donnée utilisateur reçue");
        
        setFormData(prev => ({ ...prev, firstName: userData.firstName || '', lastName: userData.lastName || '', email: userData.email || '', phone: userData.phone || '', company: userData.company || '', address: userData.address || '', city: userData.city || '', postalCode: userData.postal_code || '', description: userData.description || '', services: userData.services || {}, fleet: userData.fleet || {}, isVisible: userData.isVisible !== false, notificationsEnabled: userData.notificationsEnabled !== false, }));
        setAvatarPreview(userData.avatar_url || '');
        setDocuments({ cin: userData.cin_photo_url || null, insurance: userData.insurance_photo_url || null, registration: userData.registration_photo_url || null });
      } catch (error) {
        console.error('Erreur chargement profil:', error);
        toast.error('Impossible de charger les données du profil.');
      } finally {
        setIsLoading(false);
      }
    };
    loadUserData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (category, key) => {
    setFormData(prev => ({
      ...prev,
      [category]: { ...prev[category], [key]: !prev[category][key] }
    }));
  };
  
  const handleToggle = (key) => {
    // Désactivé - le déménageur ne peut plus modifier directement
    return;
  };
  
  const handleRequestModification = (fieldName, fieldLabel) => {
    setRequestedField(fieldName);
    setModificationRequest(`Je souhaite modifier mon ${fieldLabel}.`);
    setShowModificationRequest(true);
  };
  
  const handleSubmitModificationRequest = async (e) => {
    e.preventDefault();
    if (!modificationRequest.trim()) {
      toast.error('Veuillez décrire les modifications demandées');
      return;
    }
    
    setIsSubmitting(true);
    try {
      // Envoyer la demande à l'administrateur
      await api.sendModificationRequest({
        userId: user.id,
        field: requestedField,
        currentValue: formData[requestedField],
        request: modificationRequest,
        timestamp: new Date().toISOString()
      });
      
      toast.success('Votre demande de modification a été envoyée à l\'administrateur');
      setShowModificationRequest(false);
      setModificationRequest('');
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      toast.error('Une erreur est survenue lors de l\'envoi de la demande');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleCancelModification = () => {
    setShowModificationRequest(false);
    setModificationRequest('');
    setRequestedField('');
  };
  
  const handleAvatarChange = async (e) => {
    // ... votre logique de changement d'avatar reste la même
     const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La taille du fichier ne doit pas dépasser 5MB');
      return;
    }
    const toastId = toast.loading('Téléchargement de l\'avatar...');
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const response = await api.uploadAvatar(formData);
      if (response.avatarUrl) {
        setAvatarPreview(response.avatarUrl);
        updateUser({ ...user, avatar: response.avatarUrl });
        toast.update(toastId, { render: "Photo de profil mise à jour !", type: "success", isLoading: false, autoClose: 3000 });
      }
    } catch (error) {
      toast.update(toastId, { render: "Erreur lors du téléchargement.", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  const handleDocumentUpload = async (file, type) => {
    // La logique de base est déplacée dans le composant DocumentUploader,
    // mais la fonction d'API est appelée ici.
    const response = await uploadDocumentService(file, type);
    setDocuments(prev => ({ ...prev, [type]: response.url }));
    setFormData(prev => ({ ...prev, [`${type}_url`]: response.url }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setIsSubmitting(true);
    // ... votre logique de soumission reste la même
    try {
      const dataToUpdate = { ...formData, email: undefined };
      Object.keys(dataToUpdate).forEach(key => dataToUpdate[key] === undefined && delete dataToUpdate[key]);
      const updatedUser = await api.updateProfile(dataToUpdate);
      updateUser(updatedUser);
      toast.success('Profil mis à jour avec succès !');
    } catch (error) {
        toast.error('Erreur lors de la mise à jour du profil.');
    } finally {
        setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <FiLoader className="animate-spin h-12 w-12 text-amber-500" />
      </div>
    );
  }

  // --- RENDER ---
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-white p-4 sm:p-6 lg:p-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-amber-600/10"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1),transparent_50%)]"></div>
      </div>
      
      <form onSubmit={handleSubmit} className="max-w-7xl mx-auto relative z-10">
        <ProfileHeader 
          user={formData} 
          avatar={avatarPreview} 
          onAvatarChange={handleAvatarChange} 
          onSave={handleSubmit}
          isSubmitting={isSubmitting}
        />
        
        {/* SECTION 1: INFORMATIONS PERSONNELLES */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-amber-500 to-amber-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Informations Personnelles</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Identité */}
            <Card delay={0.1}>
              <CardHeader title="Identité" icon={<FiUser />} gradient="from-blue-500 to-blue-600" />
              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <InputField 
                    id="firstName" 
                    name="firstName" 
                    value={formData.firstName} 
                    onChange={handleChange} 
                    label="Prénom" 
                    icon={<FiUser className="w-4 h-4" />}
                    disabled={true}
                  />
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleRequestModification('firstName', 'prénom')}
                    className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                  >
                    <FiEdit3 className="w-3 h-3" />
                    Modifier
                  </motion.button>
                </div>
                
                <div className="space-y-2">
                  <InputField 
                    id="lastName" 
                    name="lastName" 
                    value={formData.lastName} 
                    onChange={handleChange} 
                    label="Nom" 
                    icon={<FiUser className="w-4 h-4" />}
                    disabled={true}
                  />
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleRequestModification('lastName', 'nom')}
                    className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                  >
                    <FiEdit3 className="w-3 h-3" />
                    Modifier
                  </motion.button>
                </div>
              </div>
            </Card>

            {/* Contact */}
            <Card delay={0.2}>
              <CardHeader title="Contact" icon={<FiPhone />} gradient="from-green-500 to-green-600" />
              <div className="p-6 space-y-6">
                <div className="p-4 bg-gradient-to-r from-slate-700/60 to-slate-800/60 rounded-2xl border border-slate-600/50 backdrop-blur-sm">
                  <label className="block text-xs font-semibold text-amber-300/80 tracking-wide">Adresse e-mail (non modifiable)</label>
                  <div className="flex items-center gap-3 text-white mt-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-xl flex items-center justify-center">
                      <FiMail className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="font-medium">{formData.email}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <InputField 
                    id="phone" 
                    name="phone" 
                    type="tel" 
                    value={formData.phone} 
                    onChange={handleChange} 
                    label="Téléphone" 
                    icon={<FiPhone className="w-4 h-4" />}
                    disabled={true}
                  />
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleRequestModification('phone', 'numéro de téléphone')}
                    className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                  >
                    <FiEdit3 className="w-3 h-3" />
                    Modifier
                  </motion.button>
                </div>
              </div>
            </Card>
          </div>
        </motion.section>

        {/* SECTION 2: VÉRIFICATION TÉLÉPHONE */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-green-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Vérification du Téléphone</h2>
          </div>
          <Card delay={0.25}>
            <PhoneVerification />
          </Card>
        </motion.section>

        {/* SECTION 3: ADRESSE PROFESSIONNELLE */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-purple-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Adresse Professionnelle</h2>
          </div>
          
          <Card delay={0.3}>
            <CardHeader title="Informations de l'entreprise" icon={<FiBriefcase />} gradient="from-purple-500 to-purple-600" />
            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <InputField 
                  id="company" 
                  name="company" 
                  value={formData.company} 
                  onChange={handleChange} 
                  label="Nom de l'entreprise" 
                  icon={<FiBriefcase className="w-4 h-4" />}
                  disabled={true}
                />
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => handleRequestModification('company', 'nom de l\'entreprise')}
                  className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                >
                  <FiEdit3 className="w-3 h-3" />
                  Modifier
                </motion.button>
              </div>
              
              <div className="space-y-2">
                <InputField 
                  id="address" 
                  name="address" 
                  value={formData.address} 
                  onChange={handleChange} 
                  label="Adresse" 
                  icon={<FiMapPin className="w-4 h-4" />}
                  disabled={true}
                />
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={() => handleRequestModification('address', 'adresse')}
                  className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                >
                  <FiEdit3 className="w-3 h-3" />
                  Modifier
                </motion.button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <InputField 
                    id="city" 
                    name="city" 
                    value={formData.city} 
                    onChange={handleChange} 
                    label="Ville"
                    disabled={true}
                  />
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleRequestModification('city', 'ville')}
                    className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                  >
                    <FiEdit3 className="w-3 h-3" />
                    Modifier
                  </motion.button>
                </div>
                
                <div className="space-y-2">
                  <InputField 
                    id="postalCode" 
                    name="postalCode" 
                    value={formData.postalCode} 
                    onChange={handleChange} 
                    label="Code Postal"
                    disabled={true}
                  />
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleRequestModification('postalCode', 'code postal')}
                    className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                  >
                    <FiEdit3 className="w-3 h-3" />
                    Modifier
                  </motion.button>
                </div>
              </div>
            </div>
          </Card>
        </motion.section>

        {/* SECTION 4: SERVICES & FLOTTE */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-orange-500 to-orange-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Services & Flotte</h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card delay={0.4}>
              <CardHeader title="Services Proposés" icon={<FiTool />} gradient="from-orange-500 to-orange-600" />
              <div className="p-6 space-y-4">
                {SERVICES_OPTIONS.map(s => (
                  <div key={s.id} className="space-y-2">
                    <CheckboxOption 
                      id={s.id} 
                      label={s.label} 
                      icon={s.icon} 
                      checked={!!formData.services[s.id]} 
                      onChange={() => {}}
                      disabled={true}
                    />
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={() => handleRequestModification('services', `services (${s.label})`)}
                      className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                    >
                      <FiEdit3 className="w-3 h-3" />
                      Modifier
                    </motion.button>
                  </div>
                ))}
              </div>
            </Card>
            
            <Card delay={0.5}>
              <CardHeader title="Flotte de Véhicules" icon={<FiTruck />} gradient="from-indigo-500 to-indigo-600" />
              <div className="p-6 space-y-4">
                {TRUCK_OPTIONS.map(t => (
                  <div key={t.id} className="space-y-2">
                    <CheckboxOption 
                      id={t.id} 
                      label={t.label} 
                      checked={!!formData.fleet[t.id]} 
                      onChange={() => {}}
                      disabled={true}
                    />
                    <motion.button 
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="button"
                      onClick={() => handleRequestModification('fleet', `flotte (${t.label})`)}
                      className="w-full text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50 flex items-center justify-center gap-2"
                    >
                      <FiEdit3 className="w-3 h-3" />
                      Modifier
                    </motion.button>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </motion.section>

        {/* SECTION 5: PRÉFÉRENCES */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-teal-500 to-teal-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Préférences</h2>
          </div>
          
          <Card delay={0.6}>
            <CardHeader title="Paramètres" icon={<FiShield />} gradient="from-teal-500 to-teal-600" subtitle="Personnalisez votre expérience"/>
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-2xl border border-slate-600/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-200 tracking-wide">Visibilité du profil</span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${formData.isVisible ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                      {formData.isVisible ? 'Visible' : 'Caché'}
                    </span>
                  </div>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleRequestModification('isVisible', 'visibilité du profil')}
                    className="text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50"
                  >
                    <FiEdit3 className="inline w-3 h-3 mr-1" />
                    Modifier
                  </motion.button>
                </div>
                
                <div className="p-4 bg-gradient-to-r from-slate-700/40 to-slate-800/40 rounded-2xl border border-slate-600/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-slate-200 tracking-wide">Notifications par e-mail</span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full ${formData.notificationsEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'}`}>
                      {formData.notificationsEnabled ? 'Activées' : 'Désactivées'}
                    </span>
                  </div>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => handleRequestModification('notificationsEnabled', 'préférences de notification')}
                    className="text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors bg-amber-500/10 hover:bg-amber-500/20 px-3 py-2 rounded-lg border border-amber-500/30 hover:border-amber-500/50"
                  >
                    <FiEdit3 className="inline w-3 h-3 mr-1" />
                    Modifier
                  </motion.button>
                </div>
              </div>
            </div>
          </Card>
        </motion.section>

        {/* SECTION 6: DOCUMENTS */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-rose-500 to-rose-600 rounded-full"></div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Documents Requis</h2>
          </div>
          
          <Card delay={0.7}>
            <CardHeader 
              title="Documents téléchargés" 
              icon={<FiFileText />} 
              gradient="from-rose-500 to-rose-600"
              subtitle="Vos documents de vérification" 
            />
            <div className="p-6 space-y-6">
              {!documents.cin && !documents.insurance && !documents.registration ? (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-3xl flex items-center justify-center mx-auto mb-4 border border-slate-600/50">
                    <FiFileText className="h-10 w-10 text-slate-500" />
                  </div>
                  <p className="text-slate-400 font-medium">Aucun document téléchargé</p>
                  <p className="text-slate-500 text-sm mt-1">Vos documents apparaîtront ici</p>
                </motion.div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {documents.cin && (
                    <DocumentViewer 
                      docType={{ id: 'cin', label: "Carte d'identité (CIN)" }}
                      docUrl={documents.cin}
                    />
                  )}
                  
                  {documents.insurance && (
                    <DocumentViewer 
                      docType={{ id: 'insurance', label: "Certificat d'assurance" }}
                      docUrl={documents.insurance}
                    />
                  )}
                  
                  {documents.registration && (
                    <DocumentViewer 
                      docType={{ id: 'registration', label: "Carte grise" }}
                      docUrl={documents.registration}
                    />
                  )}
                </div>
              )}
            </div>
          </Card>
        </motion.section>
      </form>

      {/* Modale de demande de modification */}
      <AnimatePresence>
        {showModificationRequest && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="bg-gradient-to-br from-slate-800/95 to-slate-900/95 rounded-3xl p-8 w-full max-w-lg border border-slate-700/50 shadow-2xl backdrop-blur-xl"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl flex items-center justify-center">
                  <FiEdit3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white tracking-wide">Demande de modification</h3>
                  <p className="text-amber-300/80 text-sm">Décrivez les changements souhaités</p>
                </div>
              </div>
              
              <form onSubmit={handleSubmitModificationRequest} className="space-y-6">
                <div>
                  <label htmlFor="modificationRequest" className="block text-sm font-semibold text-slate-200 mb-3 tracking-wide">
                    Décrivez les modifications que vous souhaitez apporter :
                  </label>
                  <textarea
                    id="modificationRequest"
                    rows={5}
                    className="w-full px-4 py-4 bg-gradient-to-r from-slate-700/80 to-slate-800/80 border-2 border-slate-600/50 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500 transition-all duration-300 backdrop-blur-sm hover:border-slate-500/70 focus:shadow-lg focus:shadow-amber-500/20 resize-none"
                    placeholder="Veuillez décrire en détail les modifications que vous souhaitez apporter..."
                    value={modificationRequest}
                    onChange={(e) => setModificationRequest(e.target.value)}
                    required
                  />
                </div>
                
                <div className="flex justify-end space-x-4 pt-4">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={handleCancelModification}
                    className="px-6 py-3 text-sm font-semibold text-slate-300 hover:text-white bg-gradient-to-r from-slate-700/80 to-slate-800/80 hover:from-slate-600/80 hover:to-slate-700/80 rounded-2xl transition-all duration-300 border border-slate-600/50 hover:border-slate-500/70"
                    disabled={isSubmitting}
                  >
                    Annuler
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    className={`px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-600 hover:via-amber-700 hover:to-amber-800 rounded-2xl transition-all duration-300 flex items-center shadow-xl hover:shadow-amber-500/25 border-2 border-amber-400/20 ${isSubmitting ? 'opacity-70' : ''}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <FiTrendingUp className="mr-2 w-4 h-4" />
                        Envoyer la demande
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}